import {
  CRITICAL_WORKFLOW_EXPLICIT_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  MAX_PAIN_OBSERVATION_DURATION_MS,
} from './constants';
import type {LoafObservationRuntime} from './loafObservationRuntime';
import type {
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowExplicitDatadogContext,
  CriticalWorkflowExplicitObservationHandle,
  CriticalWorkflowExplicitObservationOptions,
  RecordCriticalWorkflowDurationVital,
} from './types';
import {waitForPainWindowDelivery} from './waitForPainWindowDelivery';
import {createWorkflowSamplingPolicy} from './workflowSamplingPolicy';

type LifecycleOutcome = CriticalWorkflowExplicitDatadogContext['outcome'];

interface LifecycleObservation {
  id: number;
  workflow: string;
  workflowContext?: CriticalWorkflowExplicitObservationOptions['workflowContext'];
  startTime: number;
  maxDurationMs: number;
  minStartIntervalMs: number;
  endTime?: number;
  backstopTimeoutId: number;
  backgroundedAt?: number;
  outcome?: LifecycleOutcome;
  cancelDelivery?: () => void;
}

export class LifecyclePainObserver {
  private readonly observations = new Map<number, LifecycleObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordDurationVital: RecordCriticalWorkflowDurationVital,
    private readonly samplingPolicy = createWorkflowSamplingPolicy()
  ) {}

  /**
   * Starts measuring until the caller calls complete(), for example when a hover ends.
   * If complete() is never called, maxDurationMs stops new frames from being included
   * and the collected measurements are reported with outcome: 'timed-out'.
   */
  begin(
    options: CriticalWorkflowExplicitObservationOptions
  ): CriticalWorkflowExplicitObservationHandle | undefined {
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible' ||
      !this.isValid(options) ||
      !this.samplingPolicy.canStart(options.workflow, performance.now())
    ) {
      return undefined;
    }
    const started = this.runtime.createCollector(options.maxDurationMs);
    if (started == null) {
      return undefined;
    }

    this.samplingPolicy.recordStart(options.workflow, started.startTime);
    const observation: LifecycleObservation = {
      id: started.collectorId,
      workflow: options.workflow,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      startTime: started.startTime,
      maxDurationMs: options.maxDurationMs,
      minStartIntervalMs: this.samplingPolicy.minIntervalMs(options.workflow),
      backstopTimeoutId: 0,
    };
    observation.backstopTimeoutId = window.setTimeout(
      () =>
        this.queueCompletion(
          observation,
          observation.startTime + observation.maxDurationMs,
          'timed-out'
        ),
      options.maxDurationMs
    );
    this.observations.set(observation.id, observation);

    return {
      complete: () => this.completeObservation(observation.id),
      cancel: () => this.cancelObservation(observation.id),
    };
  }

  markBackgrounded(): void {
    for (const observation of this.observations.values()) {
      observation.backgroundedAt ??= performance.now();
    }
  }

  handlePageAbandonment(): void {
    this.markBackgrounded();
    const abandonTime = performance.now();
    for (const observation of this.observations.values()) {
      if (observation.endTime == null) {
        const endTime = Math.min(
          abandonTime,
          observation.startTime + observation.maxDurationMs
        );
        if (!this.runtime.sealCollector(observation.id, endTime)) {
          this.discardObservation(observation);
          continue;
        }
        observation.endTime = endTime;
        observation.outcome = 'abandoned';
      }
      // The page may stop running now, so report the measurements already available.
      this.finishObservation(observation.id, false);
    }
  }

  private isValid(options: CriticalWorkflowExplicitObservationOptions) {
    return (
      options.workflow.length > 0 &&
      Number.isFinite(options.maxDurationMs) &&
      options.maxDurationMs > 0 &&
      options.maxDurationMs <= MAX_PAIN_OBSERVATION_DURATION_MS
    );
  }

  private completeObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    const now = performance.now();
    const cutoff = observation.startTime + observation.maxDurationMs;
    if (now >= cutoff) {
      this.queueCompletion(observation, cutoff, 'timed-out');
      return false;
    }
    this.queueCompletion(observation, now, 'completed');
    return true;
  }

  private queueCompletion(
    observation: LifecycleObservation,
    endTime: number,
    outcome: LifecycleOutcome
  ): void {
    if (observation.endTime != null) {
      return;
    }
    if (!this.runtime.sealCollector(observation.id, endTime)) {
      this.discardObservation(observation);
      return;
    }
    observation.endTime = endTime;
    observation.outcome = outcome;
    window.clearTimeout(observation.backstopTimeoutId);
    // Stop including frames that start at or after endTime. A frame already in progress
    // may finish later; wait for its timing data before calculating the report.
    observation.cancelDelivery = waitForPainWindowDelivery(deliveryComplete =>
      this.finishObservation(observation.id, deliveryComplete)
    );
  }

  private cancelObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    this.discardObservation(observation);
    return true;
  }

  private finishObservation(id: number, deliveryComplete: boolean): void {
    const observation = this.observations.get(id);
    if (observation?.endTime == null || observation.outcome == null) {
      return;
    }
    const requestedDuration = Math.max(
      0,
      observation.endTime - observation.startTime
    );
    const result = this.runtime.finalizeCollector(
      observation.id,
      requestedDuration
    );
    if (result == null) {
      this.discardObservation(observation);
      return;
    }

    this.clearObservationResources(observation);
    this.observations.delete(id);
    const {duration, measurementComplete, ...metrics} = result;
    const vital: CriticalWorkflowDurationVitalOptions = {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: {
        instrumentationVersion:
          CRITICAL_WORKFLOW_EXPLICIT_PAIN_WINDOW_INSTRUMENTATION_VERSION,
        workflow: observation.workflow,
        observationMode: 'explicit',
        outcome: observation.outcome,
        backgrounded:
          observation.backgroundedAt != null &&
          observation.backgroundedAt <= observation.startTime + duration,
        measurementComplete: deliveryComplete && measurementComplete,
        maxDurationMs: observation.maxDurationMs,
        minStartIntervalMs: observation.minStartIntervalMs,
        ...(observation.workflowContext == null
          ? {}
          : {workflowContext: observation.workflowContext}),
        ...metrics,
      },
    };
    this.recordDurationVital(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: LifecycleObservation): void {
    if (!this.observations.has(observation.id)) {
      return;
    }
    this.clearObservationResources(observation);
    this.observations.delete(observation.id);
    this.runtime.cancelCollector(observation.id);
  }

  private clearObservationResources(observation: LifecycleObservation): void {
    window.clearTimeout(observation.backstopTimeoutId);
    observation.cancelDelivery?.();
  }
}
